<template>
    <div v-if="course.id">
        <div class="relative">
            <img
                :src="bannerUrl"
                :alt="course.title"
                class="w-full h-72 object-cover"
                @error.native="bannerImageError = true"/>
        </div>

        <div class="container my-16">
            <div class="flex flex-col gap-8 md:gap-20 lg:flex-row-reverse">
                <div class="py-9 px-8 bg-teal-dark text-white">
                    <div class="sm:flex lg:block lg:w-80 mx-auto">
                        <div class="mx-auto sm:flex-shrink-0">
                            <ul class="space-y-4">
                                <li v-for="trainer in course.trainers" class="flex items-center">
                                    <div
                                        class="relative w-16 h-16  overflow-hidden rounded-full bg-gray-200">
                                        <ProfileImage :image="trainer.profile_image"/>
                                    </div>
                                    <p class="ml-10">{{ trainer.name }}</p>
                                </li>
                            </ul>
                            <table class="mt-12 text-xl font-bold text-left sm:mx-auto">
                                <tbody>
                                <tr>
                                    <th class="px-4 border-r-2 border-dotted border-green">Credits allocated</th>
                                    <td class="px-4">{{ course.credits }} credits</td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div class="flex-1 space-y-6">
                    <h1 class="text-5xl font-bold">{{ course.title }}</h1>
                    <p class="whitespace-pre-line">{{ course.description }}</p>

                    <div>
                        <h2 class="text-2xl font-bold ">Upcoming Sessions</h2>
                        <template v-if="sessionGroups.length">
                            <div v-if="hasActiveParticipationStatus" class="mt-2 text-green-700">
                                <p v-if="course.participation_status === ParticipationStatus.Current">
                                    You are currently participating in this course.
                                </p>
                                <p v-if="course.participation_status === ParticipationStatus.Completed">
                                    You have already completed this course.
                                </p>
                            </div>
                            <p v-else-if="course.type != 2" class="mt-2">
                                Apply for your preferred dates below <br>
                                <small class="text-gray-600">
                                    Please note that you cannot apply for a course within 36 hours of the start time.
                                </small>
                            </p>

                            <div v-if="sessionGroups.length" class="space-y-4 mt-6">
                                <CourseApplicationCard
                                    v-for="sessionGroup in sessionGroups"
                                    :key="sessionGroup.id"
                                    :sessionGroup="sessionGroup"
                                    @update="refresh"/>
                            </div>
                        </template>
                        <div v-else class="border-2 border-dashed border-gray-200 p-4 text-center">
                            <p>Nothing to display</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="related && false && related.length" class="mt-16 bg-gray-300 py-12" :key="course.id">
            <div class="container">

                <div class="prose max-w-none text-center">
                    <h2>Related courses</h2>
                </div>

                <CourseCarousel :courses="related" class="mt-8"/>
            </div>
        </div>
    </div>
</template>

<script>

import axios from "axios";
import dayjs from "dayjs";
import {mapGetters} from "vuex";
import Image from "@/Components/Image";
import ProfileImage from "@/Components/ProfileImage";
import Button from "@/Components/Button";
import CourseApplicationCard from "@/Components/CourseApplicationCard";
import CourseCarousel from "@/Components/CourseCarousel";
import Card from "@/Components/Card";
import {CourseParticipationStatus as ParticipationStatus} from "@/Lib/Enums";

export default {

    name: "Course Page",

    data() {
        return {
            ParticipationStatus,
            course: {},
            sessionGroups: [],
            related: [],
            bannerImageError: false,
        }
    },

    components: {
        Button,
        CourseApplicationCard,
        CourseCarousel,
        Image,
        ProfileImage,
        Card
    },

    methods: {
        setData({course, sessionGroups}) {
            this.course = course;
            this.sessionGroups = sessionGroups ? sessionGroups.data : [];
        },
        fetchRelated() {
            axios.get(route('api.courses.index'), {
                params: {
                    pathway: this.course.pathway_id,
                    per_page: 20,
                }
            })
                .then(response => {
                    this.related = response.data.data.filter(item => item.id != this.course.id);
                })
                .catch(console.warn)
        },
        refresh() {
            this.$router.push({
                name: this.$route.name,
                params: this.$route.params,
                query: _.assign({}, this.$route.query, {t: dayjs().unix()})
            });
        },
    },

    computed: {
        ...mapGetters([
            'authenticated',
            'user',
        ]),
        bannerUrl() {
            return !this.course.main_image || this.bannerImageError ? '/images/banner.jpg' : this.course.main_image.url;
        },
        hasActiveParticipationStatus() {
            if (this.course.hasOwnProperty('participation_status')) {
                return [
                    ParticipationStatus.Current,
                    ParticipationStatus.Completed,
                ].indexOf(this.course.participation_status) >= 0;
            }

            return false;
        }
    },

    watch: {
        'user.id'() {
            this.$router.push({
                name: this.$route.name,
                params: this.$route.params,
                query: _.assign({}, this.$route.query, {t: dayjs().unix()})
            });
        },
        'course.id'() {
            this.fetchRelated();
        }
    },

    async beforeRouteEnter(to, from, next) {
        const data = await fetchAllData(to);
        next(vm => vm.setData(data));
    },

    async beforeRouteUpdate(to, from) {

        const data = await fetchAllData(to);
        this.setData(data);
        if (to.params.courseId !== from.params.courseId) {
            window.scrollTo(0, 0);
        }
    },
};

const fetchAllData = async to => {

    const data = await Promise.all([
        fetchCourse(to),
        fetchSessionGroups(to),
    ])

    return {
        course: data[0].data,
        sessionGroups: data[1].data,
    };
}

const fetchCourse = async to => {
    return axios.get(route('api.courses.show', to.params.courseId), {params: to.query})
}

const fetchSessionGroups = async to => {
    return axios.get(route('api.course-session-groups.index'), {
        params: {
            course_id: to.params.courseId,
            upcoming: true,
            sort: 'start_date'
        }
    });
}

</script>
